/**
 * What-If RECALCULATING canvas state (design doc executive-view-design/
 * EXEC_WHATIF_v1_design.md §4 RECALCULATING row).
 *
 * Phase-driven, per the locked 2026-08-19 decision: calm phase labels
 * bracket a per-calculation-group progress tracker during the calc
 * phase only —
 *   'applying'    -> "Applying your changes…"
 *   'calculating' -> the analyst CalcStatus tracker (falls back to a
 *                    calm line if the calc snapshot is missing/partial)
 *   'charting'    -> "Preparing your chart…"
 *
 * Calc component — CHANGED 2026-09-05 (Brendan): the calculating phase now
 * renders the analyst `AgenticV2/CalcStatus` component directly, so the Exec
 * recalc tracker is visually identical to the analyst one. This DELIBERATELY
 * overrides the earlier 2026-08-19 "exec-calm Duplo restyle, no dense analyst
 * chrome" decision, which had a local row-list re-implementation
 * (`GroupTracker`/`RowStatusIcon`) instead of importing CalcStatus. Brendan's
 * call: "use the exact same components as in the Analyst view — those are
 * nice." (For the ES calc rows the What-If approve path runs, CalcStatus's
 * operation counters / progress bars render nothing anyway — ES rows carry no
 * op counts — so what this brings over is chiefly the analyst treatment's
 * info-tint card wrapper + glyph styling, not extra data.)
 *
 * Wire-shape adapter, RECONCILED 2026-08-20 against Agentic-API
 * app/ai/v2/executive/whatif_executor.py + app/ai/v2/forecast/edit_scenarios.py
 * (see WhatIfProgress in ../types.ts): `progress.calc` is the BARE aggregated
 * `CalcProgressRow[]` array — there is no `{title, rows}` wrapper on the exec
 * approve path (that wrapper only exists on the analyst ConversationHistory
 * `calc_progress` Turn, which the exec executor never reaches, `publish=None`).
 * CalcStatus takes `{title, rows}`, so this component supplies the title
 * (the calm calculating heading) client-side and passes `progress.calc` as
 * `rows`.
 *
 * Defensive per the task brief: a missing `progress`, a missing/null
 * `progress.calc`, or an empty/malformed array (not an array, or an array
 * whose entries lack a usable `name`) never blocks rendering — it just falls
 * back to the calm calculating-phase line, same as the other two phases.
 */
import React from 'react';

import { CalcProgressRow } from 'src/components/AgenticV2/types';

import ProcessingPanel from '../ProcessingPanel';
import { WhatIfProgress } from '../types';

interface RecalculatingStateProps {
  progress: WhatIfProgress | null | undefined;
}

const PHASE_LABEL: Record<'applying' | 'charting', string> = {
  applying: 'Applying your changes…',
  charting: 'Preparing your chart…',
};

// Client-side title for CalcStatus — the exec approve path sends no title on
// this wire (see file header), unlike the analyst calc_progress Turn's
// backend-driven title.
const CALCULATING_HEADING = 'Recalculating your forecast…';

// True only for a non-empty array whose entries are plain objects with a
// usable `name` — the one field the row list actually renders. Any other
// shape (missing, null, not an array, empty, or entries that don't look like
// CalcProgressRow) falls back to the calm line instead of a blank/broken
// tracker.
const hasUsableRows = (
  calc: WhatIfProgress['calc']
): calc is CalcProgressRow[] =>
  Array.isArray(calc) &&
  calc.length > 0 &&
  calc.every(
    (row) => row && typeof row === 'object' && typeof row.name === 'string'
  );

const RecalculatingState: React.FC<RecalculatingStateProps> = ({
  progress,
}) => {
  // Default to 'applying' — the earliest phase — when progress hasn't
  // arrived from the first poll yet (or the field is absent entirely).
  const phase = progress?.phase ?? 'applying';

  if (phase === 'applying' || phase === 'charting') {
    return <ProcessingPanel title={PHASE_LABEL[phase]} />;
  }

  // phase === 'calculating': render the analyst CalcStatus tracker when a
  // usable snapshot is present, else the calm-line fallback (never a blank
  // canvas, never a crash on a half-populated payload).
  const calc = progress?.calc;
  if (!hasUsableRows(calc)) {
    return <ProcessingPanel title={CALCULATING_HEADING} />;
  }

  return <ProcessingPanel title={CALCULATING_HEADING} rows={calc} />;
};

export default RecalculatingState;
